import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  DirectAnswer,
  CtaBand,
  Eyebrow,
  Lead,
  PrimaryLink,
  Quote,
  TextLink,
} from "@/components/site/primitives";
import { EXPERIENCE, SITE, TESTIMONIALS } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { RelatedLinks } from "@/components/site/RelatedLinks";
import { breadcrumbMeta } from "@/lib/internal-links";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { AREAS, jsonLdScript, professionalServiceSchema, serviceSchema } from "@/lib/schema";
import { contactFunnel, trackOutboundClick } from "@/lib/analytics";
import { useSectionViews, type TrackedSection } from "@/hooks/useSectionViews";

const PATH = "/technology-non-executive-director-uk";

const CTA_SOURCE_HERO = "ned_uk_hero";

/** Sections reported as read, in page order, keyed by their heading id. */
const TRACKED_SECTIONS: TrackedSection[] = [
  { id: "ned-hero-heading", name: "Hero" },
  { id: "ned-duties-heading", name: "Statutory duties" },
  { id: "ned-frc-heading", name: "UK Corporate Governance Code" },
  { id: "ned-when-heading", name: "When a board needs one" },
  { id: "ned-bring-heading", name: "What I bring to a board" },
  { id: "ned-sectors-heading", name: "Sectors" },
  { id: "ned-engagement-heading", name: "How an engagement works" },
  { id: "ned-quotes-heading", name: "What boards and founders say" },
  { id: "ned-faq-heading", name: "Common questions" },
];
const title = "Technology Non-Executive Director UK | Timothy Ng";
const description =
  "Technology risk your board cannot challenge? Non-executive director for UK technology boards: technical risk, delivery assurance, due diligence and governance.";

const FAQ = [
  {
    q: "What is a technology non-executive director?",
    a: "A non-executive director sits on the board but does not run the business day to day. A technology non-executive director brings independent challenge specifically to the technical side: whether the architecture, the delivery plan, the security posture and the technology spend stand up to scrutiny, and whether the board is being told the whole picture.",
  },
  {
    q: "Do UK non-executive directors have the same legal duties as executives?",
    a: "Yes. Section 170(1) of the Companies Act 2006 states that the general duties in sections 171 to 177 are owed by a director to the company, and the Act makes no distinction between executive and non-executive directors. A NED carries the duty to exercise reasonable care, skill and diligence under s.174 in the same way an executive director does.",
  },
  {
    q: "Does the UK Corporate Governance Code apply to a private company?",
    a: "Not formally. The FRC's 2024 Code applies to companies in the commercial companies and closed-ended investment funds categories of the UK Listing Rules. Private boards still feel it, because investors, acquirers and enterprise customers diligence against the listed standard, and Provision 29, which applies to financial years beginning on or after 1 January 2026, requires a published declaration on the effectiveness of material controls.",
  },
  {
    q: "How much time does a UK board seat take?",
    a: "Board meetings plus availability between them. Most of the value is in the conversations that happen before a decision reaches the board, so I stay reachable to the chair and the executive team rather than appearing only on meeting days.",
  },
  {
    q: "How is this different from a fractional CTO?",
    a: "A fractional CTO works inside the business, owning technical decisions and leading the team. A non-executive director sits outside it, holding the executive to account and giving the board independent assurance. I keep the two separate, and I will say which one you actually need.",
  },
  {
    q: "Which sectors do you take UK board seats in?",
    a: "Fintech, iGaming and gambling, digital health, SaaS, data and agencies, from seed-stage companies through series A and B and into corporate ownership.",
  },
];

const WHEN = [
  {
    title: "Your board cannot evidence its own controls",
    body: "Provision 29 of the 2024 Code applies to financial years beginning on or after 1 January 2026 for listed boards, and private acquirers are already asking the same question. Someone has to test whether the material technology controls actually operate, not just whether they are documented.",
  },
  {
    title: "Everything technical sits with one person",
    body: "Key-person dependency on a single technical founder or lead is a board-level risk, not an engineering one. Under s.174 it is the board's job to have taken reasonable care over it, which means naming it before it becomes a crisis.",
  },
  {
    title: "You are UK-regulated and the evidence is thin",
    body: "UK GDPR, the DSP Toolkit in health, Gambling Commission obligations and ISO 27001 all need a director who can read the underlying evidence rather than accept a summary from the team producing it.",
  },
  {
    title: "A UK raise or trade sale is coming",
    body: "Series A and B rounds and trade buyers put the technology under a spotlight, and increasingly hold private targets to listed-company governance expectations. An independent voice who has been through it beats a tidy deck.",
  },
];

const BRING = [
  {
    title: "Independent judgment, as s.173 requires",
    body: "I hold no supplier relationships and sell no development capacity, so the challenge I bring to the board is not carrying anything else with it.",
  },
  {
    title: "Technical evidence a board can test",
    body: "Architecture, security, key-person dependency and supplier lock-in, described so every director can interrogate it, not only the ones with a technical background.",
  },
  {
    title: "Control effectiveness, not control documentation",
    body: "The 2024 Code asks whether material controls operated effectively. I look at what the systems actually did, and say plainly where the answer is no.",
  },
  {
    title: "Diligence readiness for UK rounds",
    body: "Growth capital at series A and B with private equity houses and private funds, and the technical questions those processes reliably produce.",
  },
];

const SECTORS = ["Fintech", "iGaming and gambling", "Digital health", "SaaS", "Data", "Agencies"];

const ADVISORY_ROLES = EXPERIENCE.filter(
  (item) => item.role.includes("Advisor") || item.role.includes("advisor"),
);

export const Route = createFileRoute("/technology-non-executive-director-uk")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta(
        "technology-non-executive-director-uk",
        "Technology non-executive director for UK boards | Timothy Ng",
      ),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      jsonLdScript(
        professionalServiceSchema({
          path: PATH,
          name: "Technology non-executive director | Timothy Ng, Scryla",
          areaServed: AREAS.uk,
          serviceType: "Technology non-executive director",
        }),
      ),
      jsonLdScript(
        serviceSchema({
          path: PATH,
          serviceType: "Technology non-executive director",
          areaServed: AREAS.uk,
          offerNames: [
            "Technology non-executive director appointments",
            "Board-level technology risk review",
            "Technical due diligence for investors",
          ],
        }),
      ),
      breadcrumbMeta(PATH),
    ],
  }),
  component: TechnologyNedPage,
});

function TechnologyNedPage() {
  useSectionViews(PATH, TRACKED_SECTIONS);

  return (
    <div>
      <section
        aria-labelledby="ned-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Breadcrumbs path={PATH} className="mb-6" />
          <Eyebrow>Non-executive director, UK</Eyebrow>
          <h1
            id="ned-hero-heading"
            className="mt-4 max-w-[20ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            Technology non-executive director, UK.
          </h1>
          <DirectAnswer className="mt-6">
            A technology non-executive director is a board-level appointment that gives directors
            independent scrutiny of technology risk, delivery claims and spend. In the UK the role
            carries the same statutory directors&rsquo; duties as an executive director under the
            Companies Act 2006, including liability.
          </DirectAnswer>
          <Lead className="mt-5">
            Board hearing technology updates it has no way to challenge? I take non-executive and
            advisory seats with technology-led businesses across the UK. Former CTO and COO, and
            founder of my own consultancy since 2014, so I read both the technical detail and the
            commercial pressure on the executive team.
          </Lead>
          <div className="mt-8 flex flex-wrap gap-3">
            <PrimaryLink to="/contact" onClick={() => contactFunnel.ctaClick(CTA_SOURCE_HERO)}>
              Arrange a conversation
            </PrimaryLink>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutboundClick("linkedin", CTA_SOURCE_HERO)}
              className="inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background px-6 py-3.5 text-[15px] font-semibold transition-colors hover:border-primary hover:text-primary"
            >
              View my LinkedIn profile
            </a>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="ned-duties-heading">
        <Container>
          <h2
            id="ned-duties-heading"
            className="max-w-[30ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            In the UK, a NED carries the same statutory duties as an executive
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body md:text-[19px]">
            <p>
              Section 170(1) of the Companies Act 2006 states that the general duties in sections
              171 to 177 are owed by <em>a director</em> to the company. The Act draws no
              distinction between executive and non-executive directors, so the seven general duties
              apply in full to a part-time board appointment:
            </p>
            <ol className="ml-5 list-decimal space-y-1.5 text-[15px] md:text-base">
              <li>s.171: act within powers</li>
              <li>s.172: promote the success of the company</li>
              <li>s.173: exercise independent judgment</li>
              <li>s.174: exercise reasonable care, skill and diligence</li>
              <li>s.175: avoid conflicts of interest</li>
              <li>s.176: not accept benefits from third parties</li>
              <li>s.177: declare interest in a proposed transaction or arrangement</li>
            </ol>
            <p>
              That matters when you appoint a technology NED. The seat is not an advisory
              conversation with a title attached; it is a directorship with liability, and s.174 in
              particular is why a board benefits from someone who can actually read the technical
              evidence rather than accept the assurance.{" "}
              <a
                href="https://www.legislation.gov.uk/ukpga/2006/46/part/10/chapter/2"
                onClick={() => trackOutboundClick("legislation.gov.uk", "ned_uk_duties")}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline underline-offset-4 hover:text-primary"
              >
                Companies Act 2006, Part 10, Chapter 2
              </a>
              .
            </p>
          </div>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="ned-frc-heading">
        <Container>
          <Eyebrow>The 2024 Code, and why it reaches beyond listed boards</Eyebrow>
          <h2
            id="ned-frc-heading"
            className="mt-3 max-w-[28ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Internal controls are now a board declaration
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body">
            <p>
              The FRC published the UK Corporate Governance Code 2024 on 22 January 2024. It applies
              to companies in the commercial companies and closed-ended investment funds categories
              of the UK Listing Rules, from accounting periods beginning on or after 1 January 2025
              , with the exception of Provision 29, which applies to financial years beginning on or
              after 1 January 2026.
            </p>
            <p>
              Provision 29 requires the board to monitor the risk management and internal control
              framework, review its effectiveness at least annually across all material controls,
              financial, operational, reporting and compliance, and then publish a declaration of
              effectiveness as at the balance sheet date, naming any material control that was not
              operating effectively and the remedial action taken.
            </p>
            <p>
              Most of my clients are private and outside the Code&rsquo;s formal scope. It still
              matters to them, because acquirers, investors and enterprise customers increasingly
              diligence private companies against the listed standard, and because a material
              control that is not operating effectively is very often a technology control. That is
              precisely the evidence a technology NED is there to read.{" "}
              <a
                href="https://www.frc.org.uk/library/standards-codes-policy/corporate-governance/uk-corporate-governance-code/"
                onClick={() => trackOutboundClick("frc.org.uk", "ned_uk_frc")}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline underline-offset-4 hover:text-primary"
              >
                FRC, UK Corporate Governance Code 2024
              </a>
              .
            </p>
          </div>
          <RelatedLinks
            className="mt-7"
            paths={[
              "/board-advisory",
              "/what-a-technology-non-executive-director-does",
              "/technical-due-diligence-investors-uk",
            ]}
          />
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="ned-when-heading">
        <Container>
          <Eyebrow>When a board needs one</Eyebrow>
          <h2
            id="ned-when-heading"
            className="mt-3 max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            The moments this role earns its place
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2">
            {WHEN.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="ned-bring-heading">
        <Container>
          <h2
            id="ned-bring-heading"
            className="max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            What I bring to a board
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2">
            {BRING.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="ned-sectors-heading">
        <Container>
          <Eyebrow>Sectors and experience</Eyebrow>
          <h2
            id="ned-sectors-heading"
            className="mt-3 max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Where I have held these seats
          </h2>
          <div className="mt-8 grid gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="min-w-0 lg:col-span-7">
              <h3 className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                Advisory and board roles
              </h3>
              <ol className="mt-5 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
                {ADVISORY_ROLES.map((item) => (
                  <li
                    key={`${item.org}-${item.period}`}
                    className="grid grid-cols-[minmax(0,1fr)] gap-1 px-6 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-baseline sm:gap-6"
                  >
                    <div className="min-w-0">
                      <h4 className="text-[15px] font-semibold text-ink">{item.role}</h4>
                      <p className="text-sm text-body">{item.org}</p>
                    </div>
                    <p className="shrink-0 text-sm text-muted-foreground sm:text-right">
                      {item.period}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
            <div className="lg:col-span-5">
              <h3 className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                Sectors
              </h3>
              <ul className="mt-5 flex flex-wrap gap-2">
                {SECTORS.map((sector) => (
                  <li
                    key={sector}
                    className="rounded-sm border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-body"
                  >
                    {sector}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] leading-relaxed text-body">
                Based in {SITE.location}, working with boards across the UK.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="ned-engagement-heading">
        <Container>
          <h2
            id="ned-engagement-heading"
            className="max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            How the engagement works
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body">
            <p>
              Board meetings, plus availability to the chair and the executive team between them.
              Most of the value lands before a decision reaches the board, so being reachable
              matters more than the meeting itself.
            </p>
            <p>
              I write things down. A conversation is useful; a written read your board and your
              development team can both act on is more useful.
            </p>
            <p>
              I have no development team to keep busy and no supplier referrals to protect, so the
              advice has nothing attached to it. If the seat is not the right fit, I will say so.
            </p>
          </div>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="ned-quotes-heading">
        <Container>
          <h2 id="ned-quotes-heading" className="sr-only">
            What boards say
          </h2>
          <div className="grid gap-5 md:grid-cols-2">
            <Quote item={TESTIMONIALS.steveKellyShort} />
            <Quote item={TESTIMONIALS.richardGregory} />
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="ned-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="ned-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Non-executive director FAQs
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
        </Container>
      </section>

      <CtaBand
        heading="Looking for a technology non-executive director?"
        body="Tell me about the business, the stage it is at, and what the board is missing. If it is a fit I will say so, and if it is not I will tell you that too."
        to="/contact"
        cta="Arrange a conversation"
        onCtaClick={() => contactFunnel.ctaClick("ned_uk_footer")}
      />
    </div>
  );
}
